import EquationInput from "../../../../components/inputs/EquationInput";
import { BlockMath, InlineMath } from "react-katex";
import { checkCircleEquation } from "../../../../utils/circleEquation";
import { formatLatexPreview } from "../../../../components/inputs/FractionInput";
import GraphViewer from "../../../../components/GraphPlotter/GraphViewer";
import { CIRCLE_GRAPH_COLORS, getCircleGraphRanges } from "../../../../components/GraphPlotter/circleGraphRanges";

function gcd(a, b) {
  while (b !== 0) [a, b] = [b, a % b];
  return Math.abs(a);
}

function formatMixedFractionLatex(value) {
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  const intPart = Math.floor(abs);
  const frac = abs - intPart;

  if (frac === 0) return `${sign}${intPart}`;

  // Szukamy ułamka skracalnego z dokładnością do 1/100
  const maxDen = 10; // np. 10 → pozwala na 1/2, 1/3, ..., 9/10
  for (let d = 1; d <= maxDen; d++) {
    const n = Math.round(frac * d);
    if (Math.abs(n / d - frac) < 0.01) {
      const g = gcd(n, d);
      const num = n / g;
      const den = d / g;

      if (intPart === 0) return `${sign}\\dfrac{${num}}{${den}}`;
      return `${sign}${intPart}\\dfrac{${num}}{${den}}`;
    }
  }

  // fallback jeśli nie znaleziono ładnej postaci
  return `${sign}${abs.toFixed(2)}`;
}

function simplifySqrt(n) {
  let a = 1;
  let b = n;
  for (let i = 2; i * i <= n; i++) {
    while (b % (i * i) === 0) {
      b = b / (i * i);
      a = a * i;
    }
  }
  if (b === 1) return `${a}`;
  if (a === 1) return `\\sqrt{${b}}`;
  return `${a}\\sqrt{${b}}`;
}
function formatShifted(variable, value) {
  if (value === 0) return `${variable}^2`;
  if (value > 0) return `(${variable} - ${value})^2`;
  return `(${variable} + ${Math.abs(value)})^2`;
}

// 👉 LOKALNA FUNKCJA DO LOSOWANIA
function getRandomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

const INTEGER_RADIUS_OFFSETS = [
  [3, 4],
  [4, 3],
  [6, 8],
  [8, 6],
  [5, 12],
  [12, 5],
];

const RADICAL_RADIUS_OFFSETS = Array.from({ length: 6 }, (_, x) => x + 1)
  .flatMap((x) => Array.from({ length: 6 }, (_, y) => [x, y + 1]))
  .filter(([x, y]) => x !== y && !Number.isInteger(Math.sqrt(x ** 2 + y ** 2)));

function getRadiusOffset(usesRadicalRadius) {
  const offsets = usesRadicalRadius ? RADICAL_RADIUS_OFFSETS : INTEGER_RADIUS_OFFSETS;
  const [absoluteDx, absoluteDy] = offsets[getRandomInt(0, offsets.length - 1)];
  return {
    dx: absoluteDx * (getRandomInt(0, 1) === 0 ? -1 : 1),
    dy: absoluteDy * (getRandomInt(0, 1) === 0 ? -1 : 1),
  };
}

export const id = "WriteCircleFromCenterAndPointTask";
export const displayName = "Równanie okręgu – środek i punkt";

export function generateTask() {
  const usesRadicalRadius = Math.random() < 0.2;
  const center = {
    x: getRandomInt(-5, 5),
    y: getRandomInt(-5, 5),
  };

  const { dx, dy } = getRadiusOffset(usesRadicalRadius);
  const point = {
    x: center.x + dx,
    y: center.y + dy,
  };
  const radiusSquared = dx ** 2 + dy ** 2;

  const numericRadius = Math.sqrt(radiusSquared);

  const left = `${formatShifted("x", center.x)} + ${formatShifted("y", center.y)}`;
  const answerLatex = `${left} = ${radiusSquared}`;

  return {
    type: "WriteCircleFromCenterAndPointTask",
    data: {
      center,
      point,
      radiusSquared,
      numericRadius,
      radiusKind: usesRadicalRadius ? "radical" : "integer",
    },
    answer: answerLatex,
  };
}



export function renderTask(task) {
  const { center, point } = task.data;

  return (
    <div className="space-y-4">
      <p>
        Wyznacz równanie okręgu o środku w punkcie{" "}
        <InlineMath math={`S = (${center.x},\\ ${center.y})`} /> i przechodzącego przez punkt{" "}
        <InlineMath math={`P = (${point.x},\\ ${point.y})`} />.
      </p>
    </div>
  );
}


export function renderAnswer(task) {
  return (
    <div style={{ textAlign: "left" }}>
      <InlineMath math={task.answer} />
    </div>
  );
}


export function renderUserAnswer(userInput) {
  if (!userInput || typeof userInput !== "string" || userInput.trim() === "") {
    return <i>(brak odpowiedzi)</i>;
  }

  return (
    <div style={{ textAlign: "left" }}>
      <div style={{ display: "inline-block" }}>
        <BlockMath math={userInput} />
      </div>
    </div>
  );
}


export function renderExplanation(task) {
  const { center, point, numericRadius, radiusSquared } = task.data;
  const { x, y } = center;
  const { x: px, y: py } = point;

  const dx = px - x;
  const dy = py - y;
  const { xRange, yRange, graphSize } = getCircleGraphRanges({
    circles: [{ center, radius: numericRadius }],
    points: [center, point],
  });
  const dxLatex = `(${px >= 0 ? px : `(${px})`} - ${x >= 0 ? x : `(${x})`})`;
  const dyLatex = `(${py >= 0 ? py : `(${py})`} - ${y >= 0 ? y : `(${y})`})`;

  return (
    <div className="space-y-4" style={{ textAlign: "left" }}>
      <div>
        <strong>Krok 1:</strong> Liczymy promień jako odległość między środkiem a punktem:
        <BlockMath math={`r = \\sqrt{${dxLatex}^2 + ${dyLatex}^2}`} />
        <BlockMath math={`r = \\sqrt{${dx ** 2} + ${dy ** 2}} = \\sqrt{${radiusSquared}}`} />
      </div>

      <div>
        <strong>Krok 2:</strong> Podstawiamy do wzoru kanonicznego:
        <BlockMath math="(x - a)^2 + (y - b)^2 = r^2" />
        <BlockMath math={task.answer} />
      </div>

      <div>
        <strong>Wizualizacja:</strong>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <GraphViewer
            circles={[
              {
                center: { x, y },
                radius: numericRadius,
                color: CIRCLE_GRAPH_COLORS.circle,
                label: "okrąg",
              },
            ]}
            points={{
              S: { x, y, color: CIRCLE_GRAPH_COLORS.point, textColor: CIRCLE_GRAPH_COLORS.point },
              P: { x: px, y: py, color: CIRCLE_GRAPH_COLORS.point, textColor: CIRCLE_GRAPH_COLORS.point },
            }}
            segments={[
              {
                p1: "S",
                p2: "P",
                label: "|SP|",
                color: CIRCLE_GRAPH_COLORS.segment,
              },
            ]}
            xRange={xRange}
            yRange={yRange}
            width={graphSize}
            height={graphSize}
            showGrid
          />
        </div>
      </div>
    </div>
  );
}



export function checkAnswer(task, userInput) {
  return checkCircleEquation(userInput, task.answer);
}


export function renderInput(task, userInput, setUserInput, disabled) {
  return (
    <EquationInput
      value={userInput || ""}
      onChange={(val) => setUserInput(val)}
      disabled={disabled}
      placeholder="(x - a)^2 + (y - b)^2 = r^2"
    />
  );
}
